'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import ThemeElementList from './ThemeElementList'
import { javascript } from '@codemirror/lang-javascript'
import { css } from '@codemirror/lang-css'
import { markdown } from '@codemirror/lang-markdown'
import previewTheme from './utils/previewTheme'


import { EditorView } from '@codemirror/view'
import Tabs from '@/components/Tabs/Tabs'
import { Elements, FetchedTheme, SelectedTheme } from '@/types/theme'
import { Button } from '../../UiElements'
import Modal from '@/components/Modal/Modal'
import AddThemeElement from './AddThemeElement'
import { useSnapshot } from 'valtio'
import store from '@/store'
import sortElements from '@/components/Theme/ThemeEditor/mapper/themeSort'
import getElementStatus from '@/components/Theme/ThemeEditor/utils/getElementStatus'
import themeSaveMapper from '@/components/Theme/ThemeEditor/mapper/themeSaveMapper'
//import themeMapper from "@/components/Theme/ThemeEditor/mapper/themeMapper";
import updateOriginalFromCurrent from '@/components/Theme/ThemeEditor/utils/updateOriginalFromCurrent'
import updateTheme from '@/services/ThemeService/updateTheme'
import { startNotif, closeNotif } from '@/components/DynamicForm/util/notifier'


export default function ThemeEditor() {


	const { selectedTheme } = useSnapshot(store)

	const theme = useMemo((): SelectedTheme => {
		return JSON.parse(JSON.stringify(selectedTheme || {}))
	}, [selectedTheme])


	const { currentElements, selectedElementId } = theme

	const selectedElement = useMemo(() => {
		return currentElements?.find(i => i.id === selectedElementId) || null
	}, [selectedElementId, currentElements])

	const unSavedElementsCount = useMemo(() => {
		return currentElements?.filter(i => i.status !== 'saved').length || 0
	}, [currentElements])

	const [selectedTab, setSelectedTab] = useState(0)

	const [isAddModalOpen, setIsAddModalOpen] = useState(false)



	const handleOnSelectedTabChange = (tabIndex: number) => {
		setSelectedTab(tabIndex)
	}



	const getTabs = () => {
		const selectedElement = currentElements?.find(i => i.id === selectedElementId)
		if (!selectedElement) return []
		return selectedElement.name === 'Main Css'
			? [{ name: 'CSS' }] :
			selectedElement.name === 'Tour Steps' ?
				[{name: 'Steps'}]
			: [
				{ name: 'JSX' },
				{ name: 'CSS' },
				{ name: 'Mobile CSS' },
				{ name: 'Tablet CSS' },
				{ name: 'Note' },
			]
	}



	const handleAddNewFile = useCallback((data: Elements) => {
		const list = sortElements([...currentElements, data])
		store.selectedTheme.selectedElementId = data.id
		store.selectedTheme.currentElements = list
	}, [currentElements])


	const handlePreviewTheme = () => {
		previewTheme(theme.id)
	}



	const handleOnChange = (type: 'jsx' | 'css' | 'mobileCss' | 'tabletCss' | 'note', val: string) => {

		if (!selectedElementId) return
		const editedElement = { ...selectedElement } as Elements
		editedElement[type] = val
		editedElement.status = getElementStatus(selectedElementId, type, val)

		const index = currentElements.findIndex(i => i.id === selectedElementId)
		store.selectedTheme.currentElements[index] = editedElement
	}

	const handleSaveElements = async (single?: boolean) => {
		store.loadingPage = true
		store.loadingPageClassNames = '!bg-opacity-[0.7]'
		const toastId = startNotif(single ? 'Saving Theme Element...' : 'Saving Theme...')
		const saveList: FetchedTheme = themeSaveMapper(single)
		console.log('saveList', saveList)
		const save = await updateTheme(saveList)
		store.loadingPage = false
		store.loadingPageClassNames = ''
		if (save.message === 'updated') {
			closeNotif(toastId, single ? 'Element Saved successfully :)' : 'Theme Saved successfully :)')
			updateOriginalFromCurrent(single)
		} else {
			closeNotif(toastId, single ? 'Element Save Failed :(' : 'Theme Save Failed :(', { type: 'error' })
		}
	}

	useEffect(() => {
		handleOnSelectedTabChange(0)
	}, [selectedElementId])



	return (
		<div className="flex bg-white relative flex-row">
			<style>
				{`
                    .cm-editor, cm-theme-light {
                        height: 100%;
                        width: 100%;
                        outline: none !Important;
                    }
                    html, body {
                        overscroll-behavior: none !Important;
                        overscroll-behavior-y: none !Important;
                    }
                   
                `}
			</style>
			<Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={`Add New Element`} closeOnOutsideClick={false}>
				{isAddModalOpen && (
					<AddThemeElement
						onClose={() => setIsAddModalOpen(false)}
						addFile={(data) => handleAddNewFile(data)}
						existFiles={currentElements}
					/>
				)}
			</Modal>


			<div className="flex w-[300px] flex-col sticky top-0  h-[100vh] overflow-y-auto z-[17]">
				{currentElements &&
					<ThemeElementList
						currentElements={currentElements}
						selectedElementId={selectedElementId}
					/>
				}
			</div>

			<main className="relative flex-grow w-[calc(100%-300px)] ">
				<div className={'min-h-full flex flex-col flex-grow'}>
					<div className={'pl-[18px] flex items-center justify-between sticky top-0 z-50 bg-white border-b border-gray-200 h-[57px]'}>
						<Tabs tabs={getTabs()} selectedTab={selectedTab} onSelectedChange={handleOnSelectedTabChange} className={''} />
						<div className={'flex items-center justify-end pr-xxl gap-sm flex-grow'}>
							<Button
								type={'alt'}
								className={'!flex-row flex !h-[35px]'}
								onClick={() => setIsAddModalOpen(true)}
							>
								<div className={'flex flex-row gap-sm'}>
									New File
								</div>
							</Button>
							<Button
								type={'alt'}
								className={'!flex-row flex !h-[35px]'}
								onClick={handlePreviewTheme}
							>
								<div className={'flex flex-row gap-sm'}>
									Preview Theme
								</div>
							</Button>

							{selectedElement?.status !== 'saved' &&
								<Button
									type={'primary'}
									className={'!flex-row flex !h-[35px]'}
									onClick={() => handleSaveElements(true)}
								>
									<div className={'flex flex-row gap-sm'}>
										<span className={'font-icon-light'}></span>Save
									</div>
								</Button>
							}
							{unSavedElementsCount > 0 &&
								(
									(unSavedElementsCount > 1) ||
									(unSavedElementsCount === 1 && selectedElement?.status === 'saved')
								) &&
								<Button
									type={'primary'}
									className={'!flex-row flex !h-[35px]'}
									onClick={() => handleSaveElements()}
								>
									<div className={'flex flex-row gap-sm'}>
										<span className={'font-icon-light'}></span>Save All ({unSavedElementsCount})
									</div>
								</Button>
							}


							{/*<span
                                className={`transition-all duration-100 font-icon-regular w-[37px] h-[37px] flex items-center justify-center text-[16px] font-normal text-gray-500 hover:text-gray-900 cursor-pointer`}
                                onClick={() => setExpanded(!expanded)}
                            >
                                
                            </span>*/}
						</div>
					</div>
					<div className={'min-h-[100%] w-[calc(100vw-300px)] flex-grow flex flex-col'}>

						<>
							<div className={`${selectedTab === 0 ? '' : 'hidden'} h-full pb-[100px] flex-grow`}>
								{selectedElement?.name === 'Main Css' ? (
									<CodeMirror
										value={selectedElement?.css} extensions={[css()]}
										onChange={(value) => {
											handleOnChange('css', value)
										}}
										className={'min-h-full border-b border-gray-300 outline-none'}
									/>
								) : (
									<CodeMirror
										value={selectedElement?.jsx} extensions={[javascript({ jsx: true })]}
										onChange={(value) => {
											handleOnChange('jsx', value)
										}}
										className={'min-h-full border-b border-gray-300 outline-none'}
									/>
								)}

							</div>

							<div className={`${selectedTab === 1 ? '' : 'hidden'}`}>
								<CodeMirror
									value={selectedElement?.css} extensions={[css()]}
									onChange={(value) => {
										handleOnChange('css', value)
									}}
									className={'min-h-full border-b border-gray-300 outline-none'}
								/>
							</div>

							<div className={`${selectedTab === 2 ? '' : 'hidden'}`}>
								<CodeMirror
									value={selectedElement?.mobileCss} extensions={[css()]}
									onChange={(value) => {
										handleOnChange('mobileCss', value)
									}}
									className={'min-h-full border-b border-gray-300 outline-none'}
								/>
							</div>

							<div className={`${selectedTab === 3 ? '' : 'hidden'}`}>
								<CodeMirror
									value={selectedElement?.tabletCss} extensions={[css()]}
									onChange={(value) => {
										handleOnChange('tabletCss', value)
									}}
									className={'min-h-full border-b border-gray-300 outline-none'}
								/>
							</div>

							<div className={`${selectedTab === 4 ? '' : 'hidden'}`}>
								<CodeMirror
									value={selectedElement?.note} extensions={[markdown(), EditorView.lineWrapping]}
									onChange={(value) => {
										handleOnChange('note', value)
									}}
									className={'min-h-full border-b border-gray-300 outline-none'}
								/>
							</div>

						</>
					</div>
				</div>
			</main>

		</div>
	)
}
